// Pure helpers for the New notification page (spec 044 D41–D48): the form's shape, its words,
// its checks, the payload it sends and the summary, confirm and preview texts. No React, no
// MUI, no Firebase: Jest loads this file. Every "now" is a parameter so the tests can pin it.
import { toDate } from "./ticketPresentation";
import { parts, timeLabel, whenLabel } from "./queuePresentation";
import { countText } from "./kitPresentation";
import { AGE_RANGES, STATUS, openedLabel } from "./announcementPresentation";

export const TITLE_MAX = 65;
export const BODY_MAX = 240;
export const PEOPLE_MAX = 500;
export const NAME_TOKEN = "{name}";
// The server refuses a schedule under 2 minutes or over a year ahead; the page says so first.
export const MIN_AHEAD_MS = 2 * 60 * 1000;
export const MAX_AHEAD_MS = 365 * 24 * 60 * 60 * 1000; // the server's own limit

export const emptyForm = () => ({
  role: "student", // "student" | "teacher" (D41)
  kind: "all", // "all" | "specific"
  people: [], // [{ id, name, email }] when specific
  filterOn: false, // the gender/age switch, for "all"
  gender: "all", // "all" | "male" | "female"
  age: "all", // an AGE_RANGES key; students only
  title: "",
  body: "",
  linkOn: false, // the optional Link field is shown
  url: "",
  timing: "now", // "now" | "schedule"
  date: "", // "2026-10-02", the reader's local day
  time: "", // "18:00", the reader's local time
});

const pad = (n) => String(n).padStart(2, "0");
// A Date as the native date and time inputs hold it, in local time.
export const dateInputValue = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const timeInputValue = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

// A stored draft or scheduled announcement back into the form. The chosen people arrive as
// ids; the page reads their names.
export const formFromAnnouncement = (a) => {
  const audience = a.audience || {};
  const age = AGE_RANGES.find((r) => r.min === audience.ageMin && r.max === audience.ageMax && r.key !== "all");
  const sendAt = toDate(a.sendAt);
  return {
    ...emptyForm(),
    role: audience.role === "teacher" ? "teacher" : "student",
    kind: audience.kind === "specific" ? "specific" : "all",
    people: (audience.uids || []).map((id) => ({ id, name: "", email: "" })),
    filterOn: Boolean(audience.gender) || Boolean(age),
    gender: audience.gender || "all",
    age: age ? age.key : "all",
    title: a.title || "",
    body: a.body || "",
    linkOn: Boolean(a.url),
    url: a.url || "",
    timing: sendAt ? "schedule" : "now",
    date: sendAt ? dateInputValue(sendAt) : "",
    time: sendAt ? timeInputValue(sendAt) : "",
  };
};

// The words that follow the role (D41).
export const roleCopy = (role) => {
  const t = role === "teacher";
  const one = t ? "teacher" : "student";
  const many = t ? "teachers" : "students";
  return {
    one,
    many,
    choose: `Choose ${many}`,
    chooseSub: `You can choose all ${many} or specific ${many}.`,
    all: `All ${many}`,
    allSub: `Send the notification to all registered ${many}`,
    specific: `Specific ${many}`,
    specificSub: `Send notification to specific ${many} only`,
    filter: t ? "Filter by gender" : "Filter by gender and age",
    searchPlaceholder: `Search for a ${one} by name or email address...`,
    previewLine: `This is an example of how the notification appears to ${many}.`,
  };
};

// "312 students" · "1 teacher"; a count that is not a number is shown as it is ("…", "—").
export const peopleLabel = (count, role) => {
  const { one, many } = roleCopy(role);
  if (typeof count !== "number") return `${count} ${many}`;
  return `${countText(count)} ${count === 1 ? one : many}`;
};

const GENDER_WORD = { male: "male", female: "female" };
const ageWords = (key) => {
  const r = AGE_RANGES.find((x) => x.key === key);
  if (!r || r.key === "all") return "";
  if (r.key === "under7") return "under 7";
  if (r.key === "18plus") return "ages 18 and over";
  return `ages ${r.min} – ${r.max}`;
};

// The purple line under the filter: "312 students match — male, ages 10 – 13". Nothing after
// the dash while neither filter is set.
export const matchLine = (count, form) => {
  const details = [GENDER_WORD[form.gender], form.role === "student" ? ageWords(form.age) : ""].filter(Boolean).join(", ");
  return `${peopleLabel(count, form.role)} match${details ? ` — ${details}` : ""}`;
};

// The audience the server receives (D41, D42): the gender and age filter only for "all" with
// the switch on; teachers have no age.
export const audienceFromForm = (form) => {
  const specific = form.kind === "specific";
  const filtered = !specific && form.filterOn;
  const age = filtered && form.role === "student" ? AGE_RANGES.find((r) => r.key === form.age && r.key !== "all") : null;
  return {
    role: form.role,
    kind: specific ? "specific" : "all",
    uids: specific ? form.people.map((p) => p.id) : [],
    gender: filtered && GENDER_WORD[form.gender] ? form.gender : null,
    ageMin: age ? age.min : null,
    ageMax: age ? age.max : null,
  };
};

// Everyone, or at least one chosen person (D48).
export const audienceComplete = (form) => form.kind === "all" || form.people.length > 0;

// The chosen moment, in the reader's local time; null until both fields hold something valid.
export const sendAtFromForm = (form) => {
  if (!form.date || !form.time) return null;
  const d = new Date(`${form.date}T${form.time}`);
  return Number.isNaN(d.getTime()) ? null : d;
};

// The line under the schedule panel: "" while the time is fine.
export const scheduleWarning = (form, now) => {
  if (form.timing !== "schedule") return "";
  const at = sendAtFromForm(form);
  if (!at) return "Pick a date and time.";
  const ahead = at.getTime() - now.getTime();
  if (ahead < MIN_AHEAD_MS) return "Pick a time at least 2 minutes from now.";
  if (ahead > MAX_AHEAD_MS) return "Pick a time within the next year.";
  return "";
};

export const LINK_RULE = /^https:\/\/\S+$/;
export const linkWarning = (form) => (form.linkOn && form.url.trim() && !LINK_RULE.test(form.url.trim()) ? "The link must start with https://" : "");

// Whether the primary button may go (D48): a title, a message, a complete audience, a link
// that is a link, and a schedule the server will take.
export const canSubmit = (form, now) =>
  Boolean(form.title.trim()) &&
  Boolean(form.body.trim()) &&
  audienceComplete(form) &&
  !linkWarning(form) &&
  !scheduleWarning(form, now);

// What saveAnnouncement receives. A draft is lenient: whatever is on the form, the schedule
// only when it parses. `sendAt` is epoch milliseconds.
export const buildPayload = (form, action, id) => {
  const at = form.timing === "schedule" ? sendAtFromForm(form) : null;
  const url = form.linkOn ? form.url.trim() : "";
  return {
    ...(id ? { id } : {}),
    action,
    title: form.title.trim(),
    body: form.body.trim(),
    url: url || null,
    audience: audienceFromForm(form),
    ...(at ? { sendAt: at.getTime() } : {}),
  };
};

// The side card's rows (D48). `opts.timeZone` pins the zone (tests).
export const summaryRows = (form, opts) => {
  const { many } = roleCopy(form.role);
  const rows = [["Audience", form.role === "teacher" ? "Teachers" : "Students"]];
  if (form.kind === "specific") rows.push(["People", form.people.length === 0 ? "None chosen" : `${form.people.length} chosen`]);
  else {
    rows.push(["Gender", form.filterOn && GENDER_WORD[form.gender] ? (form.gender === "male" ? "Male" : "Female") : "All"]);
    if (form.role === "student") rows.push(["Age range", form.filterOn ? AGE_RANGES.find((r) => r.key === form.age)?.label || "All ages" : "All ages"]);
  }
  const at = form.timing === "schedule" ? sendAtFromForm(form) : null;
  rows.push(["Send time", form.timing === "now" ? "Now" : at ? whenLabel(at, opts) : "Not set"]);
  return rows.map(([label, value]) => ({ label, value, many }));
};

// The confirm dialog (D48): a fresh count first, then one question. Nobody to send to is a
// dead end, not a question — the server would refuse it ("Nobody matches this audience.").
export const confirmText = (form, { targeted, withDevice }, opts) => {
  if (!targeted) return { title: "Nobody matches this audience", message: "Change the audience or its filter, then try again.", blocked: true };
  const who = peopleLabel(targeted, form.role);
  if (form.timing === "schedule") {
    const at = sendAtFromForm(form);
    return { title: `Schedule for ${who} on ${at ? whenLabel(at, opts) : "—"}?`, message: "The list is checked again when it goes out." };
  }
  return { title: `Send to ${who} now?`, message: `${countText(withDevice)} of them also get a phone alert.` };
};

// The History's line after the page hands back (D40), as the router state it travels in. The
// server closes a send that reached nobody as failed, and that is never a green line.
export const successMessage = (action, form, result, opts) => {
  if (action === "draft") return { notice: "Draft saved", severity: "success" };
  if (action === "schedule") {
    const at = sendAtFromForm(form);
    return { notice: `Scheduled for ${at ? whenLabel(at, opts) : "later"}`, severity: "success" };
  }
  const { delivered = 0, failed = 0 } = result?.counts || {};
  if (!delivered) return { notice: "Sending failed. No one received it.", severity: "error" };
  const sent = `Sent to ${peopleLabel(delivered, form.role)}`;
  return failed ? { notice: `${sent} · ${countText(failed)} failed`, severity: "warning" } : { notice: sent, severity: "success" };
};

// The footer's line when a save fails. The server's refusals carry their own sentence; any
// other failure (a timeout, a dropped connection, a crash) may have come after the server
// began, and a retry is safe because the page keeps the notification's id and the server
// never sends one twice (R15). `saved`: this page already created the notification.
const REFUSALS = ["functions/invalid-argument", "functions/failed-precondition", "functions/permission-denied", "functions/not-found"];
export const saveErrorText = (e, { saved } = {}) => {
  if (!REFUSALS.includes(e?.code) || !e?.message) return "The server did not confirm the save. Try again: nothing is sent twice.";
  return saved ? `${e.message} You can find it in the History.` : e.message;
};

// The phone's lock screen: the day and the time it arrives, 24-hour (047 D81).
export const lockScreen = (at, { timeZone } = {}) => {
  const p = parts(at, { weekday: "long", month: "long", day: "numeric", timeZone });
  return { day: `${p.weekday}, ${p.month} ${Number(p.day)}`, time: timeLabel(at, { timeZone }) };
};

// The first name the preview fills in: the first chosen person's, else Ahmed.
export const sampleFirstName = (people) => {
  const name = String(people?.[0]?.name || "").trim();
  return name ? name.split(/\s+/)[0] : "Ahmed";
};

// "{name}" at the caret, if it fits in the field. Unchanged when it does not.
export const insertAtCaret = (text, caret, max) => {
  const value = String(text || "");
  if (value.length + NAME_TOKEN.length > max) return { text: value, caret: caret ?? value.length, inserted: false };
  const at = Math.min(Math.max(caret ?? value.length, 0), value.length);
  return { text: value.slice(0, at) + NAME_TOKEN + value.slice(at), caret: at + NAME_TOKEN.length, inserted: true };
};

// The line over a read-only page (D45): "Sent Thu 24 Sep, 17:30 · 310 delivered · 2 failed ·
// 72.4% opened", "Failed …", or "Sending now…".
export const statusLine = (a, opened, opts) => {
  if (a.status === "sending") return "Sending now…";
  const at = toDate(a.sentAt) || toDate(a.sendingAt);
  const counts = a.counts || {};
  const bits = [`${STATUS[a.status]?.label || a.status} ${at ? whenLabel(at, opts) : ""}`.trim()];
  if (typeof counts.delivered === "number") bits.push(`${countText(counts.delivered)} delivered`);
  if (typeof counts.failed === "number") bits.push(`${countText(counts.failed)} failed`);
  if (a.status === "sent") {
    const rate = openedLabel(a, opened);
    if (rate !== "—") bits.push(`${rate} opened`);
  }
  return bits.join(" · ");
};

// Whether a stored announcement can still be edited (D45).
export const isEditable = (a) => a?.status === "draft" || a?.status === "scheduled";
